import { WorkoutDataPoint, Workout } from '../types/workout';
import { generateWorkoutChart } from './workoutVisualization';

interface StravaTokens {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

interface StravaAthlete {
  id: number;
  username: string | null;
  resource_state: number;
  firstname: string;
  lastname: string;
  bio: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  sex: string;
  premium: boolean;
  summit: boolean;
  created_at: string;
  updated_at: string;
  badge_type_id: number;
  weight: number | null;
  profile_medium: string | null;
  profile: string | null;
  friend: null;
  follower: null;
}

interface StravaActivity {
  id: number;
  name: string;
  type: string;
  start_date: string;
  elapsed_time: number;
  moving_time: number;
  distance: number;
  average_speed: number;
  max_speed: number;
  average_watts?: number;
  max_watts?: number;
  average_heartrate?: number;
  max_heartrate?: number;
  average_cadence?: number;
}

class StravaService {
  private clientId: string;
  private clientSecret: string;
  private redirectUri: string;
  private scope = 'activity:write';

  constructor() {
    this.clientId = process.env.REACT_APP_STRAVA_CLIENT_ID || '';
    this.clientSecret = process.env.REACT_APP_STRAVA_CLIENT_SECRET || '';
    this.redirectUri =
      process.env.REACT_APP_STRAVA_REDIRECT_URI ||
      `${window.location.origin}/strava/callback`;
  }

  // Generate authorization URL for OAuth flow
  getAuthorizationUrl(): string {
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: this.scope,
      approval_prompt: 'auto',
    });

    return `https://www.strava.com/oauth/authorize?${params.toString()}`;
  }

  // Exchange authorization code for access tokens
  async exchangeCodeForTokens(
    code: string
  ): Promise<StravaTokens & { athlete?: StravaAthlete }> {
    const response = await fetch('https://www.strava.com/oauth/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: this.clientId,
        client_secret: this.clientSecret,
        code,
        grant_type: 'authorization_code',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to exchange code for tokens');
    }

    const data = await response.json();

    // Cache athlete data from the token response
    if (data.athlete) {
      this.cacheAthleteData(data.athlete);
    }

    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
      athlete: data.athlete,
    };
  }

  // Refresh access token
  async refreshAccessToken(refreshToken: string): Promise<StravaTokens> {
    const response = await fetch('https://www.strava.com/oauth/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        client_id: this.clientId,
        client_secret: this.clientSecret,
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh access token');
    }

    const data = await response.json();
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
    };
  }

  // Get current athlete info with caching
  async getAthlete(accessToken: string, allowFailure: boolean = false) {
    // Check cache first
    const cachedAthlete = this.getCachedAthlete();
    if (cachedAthlete && this.isAthleteDataValid(cachedAthlete)) {
      return cachedAthlete.data;
    }

    try {
      const response = await fetch('https://www.strava.com/api/v3/athlete', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to get athlete info: ${response.status}`);
      }

      const athleteData = await response.json();

      // Cache the athlete data
      this.cacheAthleteData(athleteData);

      return athleteData;
    } catch (error) {
      // During auth flow, fail completely if no cached data
      if (!allowFailure) {
        throw error;
      }

      // If we have cached data, return it even if it's stale
      if (cachedAthlete) {
        console.warn('Using cached athlete data due to API error:', error);
        return cachedAthlete.data;
      }

      // If no cached data, fail the request
      throw error;
    }
  }

  // Cache athlete data with timestamp
  private cacheAthleteData(athleteData: any): void {
    const cachedData = {
      data: athleteData,
      timestamp: Date.now(),
      expires: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
    };
    localStorage.setItem('strava_athlete_cache', JSON.stringify(cachedData));
  }

  // Get cached athlete data
  private getCachedAthlete(): {
    data: any;
    timestamp: number;
    expires: number;
  } | null {
    const cached = localStorage.getItem('strava_athlete_cache');
    if (!cached) return null;

    try {
      return JSON.parse(cached);
    } catch {
      return null;
    }
  }

  // Check if cached athlete data is still valid
  private isAthleteDataValid(cachedAthlete: { expires: number }): boolean {
    return Date.now() < cachedAthlete.expires;
  }

  // Clear cached athlete data
  clearAthleteCache(): void {
    localStorage.removeItem('strava_athlete_cache');
  }

  // Upload activity file to Strava (includes all workout data points)
  async uploadActivity(
    accessToken: string,
    workoutData: WorkoutDataPoint[],
    startTime: number,
    totalDuration: number,
    workoutName?: string,
    workout?: Workout,
    uploadPhoto?: boolean
  ): Promise<StravaActivity> {
    // Generate TCX content with all data points
    const tcxContent = this.generateTCXContent(
      workoutData,
      startTime,
      totalDuration
    );

    // Create form data for file upload
    const formData = new FormData();

    // Create TCX file blob
    const tcxBlob = new Blob([tcxContent], { type: 'application/xml' });
    const fileName = `workout-${new Date(startTime).toISOString().split('T')[0]}.tcx`;

    formData.append('file', tcxBlob, fileName);
    formData.append('data_type', 'tcx');
    formData.append(
      'name',
      workoutName ||
        `Indoor Cycling - ${new Date(startTime).toLocaleDateString()}`
    );
    formData.append('description', 'Uploaded from turbo-trainer.com');
    formData.append('trainer', '1');
    formData.append('commute', '0');

    const response = await fetch('https://www.strava.com/api/v3/uploads', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Failed to upload activity: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`
      );
    }

    const uploadResponse = await response.json();

    // The upload endpoint returns an upload status, we need to poll for completion
    const uploadId = uploadResponse.id;

    if (!uploadId) {
      throw new Error('Upload failed: No upload ID returned');
    }

    // Poll for upload completion (with timeout)
    const maxAttempts = 30; // 30 seconds timeout
    let attempts = 0;

    while (attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second

      const statusResponse = await fetch(
        `https://www.strava.com/api/v3/uploads/${uploadId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (statusResponse.ok) {
        const status = await statusResponse.json();

        if (status.activity_id) {
          // Upload completed successfully
          const activity = {
            id: status.activity_id,
            name:
              workoutName ||
              `Indoor Cycling - ${new Date(startTime).toLocaleDateString()}`,
            type: 'Ride',
            start_date: new Date(startTime).toISOString(),
            elapsed_time: totalDuration,
            moving_time: totalDuration,
            distance: 0,
            average_speed: 0,
            max_speed: 0,
          };

          // Upload workout chart photo if enabled and workout data is provided
          if (workout && uploadPhoto !== false) {
            try {
              await this.uploadWorkoutPhoto(accessToken, status.activity_id, workout);
            } catch (error) {
              console.warn('Failed to upload workout chart:', error);
              // Don't fail the entire upload if photo upload fails
            }
          }

          return activity;
        }

        if (status.error) {
          throw new Error(`Upload failed: ${status.error}`);
        }

        // Still processing, continue polling
      }

      attempts++;
    }

    // Timeout reached
    throw new Error(
      'Upload timeout: Activity upload is taking longer than expected. Please check Strava manually.'
    );
  }

  // Upload workout chart photo to an activity
  async uploadWorkoutPhoto(
    accessToken: string,
    activityId: number,
    workout: Workout
  ): Promise<void> {
    try {
      // Generate workout chart image
      const chartBlob = await generateWorkoutChart(workout);
      
      if (!chartBlob || chartBlob.size === 0) {
        throw new Error('Failed to generate workout chart');
      }

      // Create form data for photo upload
      const formData = new FormData();
      formData.append('file', chartBlob, 'workout-chart.png');
      formData.append('description', 'Workout power profile generated by turbo-trainer.com');

      const response = await fetch(
        `https://www.strava.com/api/v3/activities/${activityId}/photos`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          `Failed to upload photo: ${response.status} ${response.statusText} - ${JSON.stringify(errorData)}`
        );
      }
    } catch (error) {
      console.error('Error uploading workout photo:', error);
      throw error;
    }
  }

  // Legacy method for backward compatibility
  async createActivity(
    accessToken: string,
    workoutData: WorkoutDataPoint[],
    startTime: number,
    totalDuration: number,
    workoutName?: string,
    workout?: Workout,
    uploadPhoto?: boolean
  ): Promise<StravaActivity> {
    return this.uploadActivity(
      accessToken,
      workoutData,
      startTime,
      totalDuration,
      workoutName,
      workout,
      uploadPhoto
    );
  }

  // Generate TCX file content for detailed upload
  generateTCXContent(
    workoutData: WorkoutDataPoint[],
    startTime: number,
    totalDuration: number
  ): string {
    const powerData = workoutData.filter((d) => d.power > 0);
    const hrData = workoutData.filter((d) => d.heartRate && d.heartRate > 0);
    const cadenceData = workoutData.filter((d) => d.cadence && d.cadence > 0);

    const avgPower =
      powerData.length > 0
        ? powerData.reduce((sum, d) => sum + d.power, 0) / powerData.length
        : 0;
    const maxPower =
      powerData.length > 0 ? Math.max(...powerData.map((d) => d.power)) : 0;
    const avgHeartRate =
      hrData.length > 0
        ? hrData.reduce((sum, d) => sum + (d.heartRate || 0), 0) / hrData.length
        : 0;
    const maxHeartRate =
      hrData.length > 0 ? Math.max(...hrData.map((d) => d.heartRate || 0)) : 0;
    const avgCadence =
      cadenceData.length > 0
        ? cadenceData.reduce((sum, d) => sum + (d.cadence || 0), 0) /
          cadenceData.length
        : 0;
    const maxCadence =
      cadenceData.length > 0
        ? Math.max(...cadenceData.map((d) => d.cadence || 0))
        : 0;

    return `<?xml version="1.0" encoding="UTF-8"?>
<TrainingCenterDatabase xmlns="http://www.garmin.com/xmlschemas/TrainingCenterDatabase/v2">
  <Activities>
    <Activity Sport="Biking">
      <Id>${new Date(startTime).toISOString()}</Id>
      <Notes>Uploaded from turbo-trainer.com</Notes>
      <Lap StartTime="${new Date(startTime).toISOString()}">
        <TotalTimeSeconds>${totalDuration}</TotalTimeSeconds>
        <Calories>0</Calories>
        ${avgHeartRate > 0 ? `<AverageHeartRateBpm><Value>${Math.round(avgHeartRate)}</Value></AverageHeartRateBpm>` : ''}
        ${maxHeartRate > 0 ? `<MaximumHeartRateBpm><Value>${maxHeartRate}</Value></MaximumHeartRateBpm>` : ''}
        <Extensions>
          <LX xmlns="http://www.garmin.com/xmlschemas/ActivityExtension/v2">
            <AvgPower>${Math.round(avgPower)}</AvgPower>
            <MaxPower>${maxPower}</MaxPower>
            ${avgCadence > 0 ? `<AvgCadence>${Math.round(avgCadence)}</AvgCadence>` : ''}
            ${maxCadence > 0 ? `<MaxCadence>${maxCadence}</MaxCadence>` : ''}
          </LX>
        </Extensions>
        <Track>
          ${workoutData
            .map(
              (point) => `
          <Trackpoint>
            <Time>${new Date(point.timestamp).toISOString()}</Time>
            ${point.heartRate ? `<HeartRateBpm><Value>${point.heartRate}</Value></HeartRateBpm>` : ''}
            <Extensions>
              <TPX xmlns="http://www.garmin.com/xmlschemas/ActivityExtension/v2">
                <Watts>${point.power}</Watts>
                ${point.cadence ? `<Cadence>${point.cadence}</Cadence>` : ''}
              </TPX>
            </Extensions>
          </Trackpoint>`
            )
            .join('')}
        </Track>
      </Lap>
    </Activity>
  </Activities>
</TrainingCenterDatabase>`;
  }

  // Store tokens securely in localStorage (in production, consider more secure storage)
  storeTokens(tokens: StravaTokens): void {
    localStorage.setItem('strava_tokens', JSON.stringify(tokens));
  }

  // Retrieve stored tokens
  getStoredTokens(): StravaTokens | null {
    const stored = localStorage.getItem('strava_tokens');
    if (!stored) return null;

    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  // Clear stored tokens and athlete cache
  clearStoredTokens(): void {
    localStorage.removeItem('strava_tokens');
    this.clearAthleteCache();
  }

  // Check if we have valid tokens
  async hasValidTokens(): Promise<boolean> {
    const tokens = this.getStoredTokens();
    if (!tokens) return false;

    // Check if token is expired (with 5 minute buffer)
    const now = Math.floor(Date.now() / 1000);
    if (tokens.expires_at <= now + 300) {
      try {
        // Try to refresh the token
        const newTokens = await this.refreshAccessToken(tokens.refresh_token);
        this.storeTokens(newTokens);
        return true;
      } catch {
        this.clearStoredTokens();
        return false;
      }
    }

    return true;
  }

  // Get current access token (refreshing if necessary)
  async getAccessToken(): Promise<string | null> {
    const tokens = this.getStoredTokens();
    if (!tokens) return null;

    // Check if token is expired (with 5 minute buffer)
    const now = Math.floor(Date.now() / 1000);
    if (tokens.expires_at <= now + 300) {
      try {
        const newTokens = await this.refreshAccessToken(tokens.refresh_token);
        this.storeTokens(newTokens);
        return newTokens.access_token;
      } catch {
        this.clearStoredTokens();
        return null;
      }
    }

    return tokens.access_token;
  }
}

export const stravaService = new StravaService();
export type { StravaTokens, StravaActivity };
